import styles from './index.module.css'

import React from 'react'

import { SEARCH_DATE_INGESTED_FILTER_FIELD } from '@disco/constants/search'

import type {
  FilterComponentProps,
  FilterOperatorOption,
} from 'types/filter-page'

import { Flex, Text, TextField } from 'components/ds'

import { useKeyPress } from 'hooks/use-key-press'

import { FilterOperatorSelector } from '../../filter-operator-selector'
import { getBetweenRangeErrors, getMissingValuePresets } from '../../utils'
import { FilterErrorText } from '../components'

const DateForm = ({
  label,
  field,
  value,
  onChange,
  operator = 'equals',
  onPress,
}: FilterComponentProps) => {
  const { handleKeyDown } = useKeyPress('Enter', onPress)

  const {
    startMissing,
    endMissing,
    isIncomplete: isBetweenIncomplete,
  } = getBetweenRangeErrors(value)

  // Helper function to create date inputs
  const createDateInput = (
    inputValue: string,
    onChangeHandler: (_value: string) => void,
    shouldAutoFocus = true,
    hasError = false,
    doubleInput = false,
  ) => (
    <TextField.Root
      type="date"
      value={inputValue || ''}
      onChange={e => onChangeHandler(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder={label}
      autoFocus={shouldAutoFocus}
      color={hasError ? 'red' : undefined}
      style={{
        width: doubleInput ? '125px' : '273px',
        ...(hasError ? { boxShadow: 'inset 0 0 0 1px var(--red-8)' } : {}),
      }}
      className={styles.dateInput}
    />
  )

  const allFilterOperators: FilterOperatorOption[] = [
    {
      operator: 'equals',
      component: createDateInput(value || '', newValue => {
        onChange(newValue, operator)
      }),
    },
    {
      operator: 'between',
      component: (
        <Flex direction="column" gap="1">
          <Flex gap="2" align="center">
            {createDateInput(
              value?.[0] || '',
              newValue => {
                const updatedValue = !newValue
                  ? ['', value?.[1] || '']
                  : [newValue, value?.[1] || '']

                onChange(updatedValue, operator)
              },
              true,
              startMissing,
              true,
            )}
            <Text size="2">-</Text>
            {createDateInput(
              value?.[1] || '',
              newValue => {
                const updatedValue = !newValue
                  ? [value?.[0] || '', '']
                  : [value?.[0] || '', newValue]

                onChange(updatedValue, operator)
              },
              false,
              endMissing,
              true,
            )}
          </Flex>
          {isBetweenIncomplete && (
            <FilterErrorText>Enter both dates</FilterErrorText>
          )}
        </Flex>
      ),
    },
    {
      operator: 'lte',
      component: createDateInput(value || '', newValue => {
        onChange(newValue, operator)
      }),
    },
    {
      operator: 'gte',
      component: createDateInput(value || '', newValue => {
        onChange(newValue, operator)
      }),
    },
  ]

  // Every track has an ingest date, so "is missing" could never match there.
  const presets =
    field === SEARCH_DATE_INGESTED_FILTER_FIELD
      ? []
      : getMissingValuePresets(label)

  return (
    <FilterOperatorSelector
      operator={operator}
      value={value}
      operators={allFilterOperators}
      presets={presets}
      onChange={onChange}
      onKeyDown={handleKeyDown}
    />
  )
}

export default DateForm
